import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ActiveBadge } from '@/modules/inventory/components';
import { useLogisticsProviders } from '@/modules/logistics/hooks';
import { saveLogisticsProvider } from '@/modules/logistics/services';
import type { LogisticsProvider, LogisticsProviderInput } from '@/modules/logistics/types';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

const empty: LogisticsProviderInput = {
  name: '',
  description: '',
  contactName: '',
  contactPhone: '',
  email: '',
  active: true,
};

export function LogisticsProvidersPage() {
  const { providers, loading, error, refresh } = useLogisticsProviders();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('logistics.write');

  const [form, setForm] = useState<LogisticsProviderInput>(empty);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  function startEdit(provider: LogisticsProvider) {
    setEditingId(provider.id);
    setForm({
      name: provider.name,
      description: provider.description,
      contactName: provider.contactName,
      contactPhone: provider.contactPhone,
      email: provider.email,
      active: provider.active,
    });
  }

  function resetForm() {
    setEditingId(null);
    setForm(empty);
    setFormError(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canWrite) return;
    if (!form.name.trim()) {
      setFormError('El nombre es obligatorio.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await saveLogisticsProvider(form, editingId ?? undefined);
      resetForm();
      await refresh();
    } catch {
      setFormError('No se pudo guardar el proveedor.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <p>
        <Link to={modulePaths.logistics}>← Órdenes logísticas</Link>
      </p>
      <h1>Proveedores logísticos</h1>

      {canWrite ? (
        <form className="inventory-panel" onSubmit={handleSubmit}>
          <h2>{editingId ? 'Editar proveedor' : 'Nuevo proveedor'}</h2>
          <div className="users-form-grid">
            <label className="auth-label">
              Nombre *
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="auth-label">
              Email
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </label>
            <label className="auth-label">
              Contacto
              <input
                value={form.contactName}
                onChange={(e) =>
                  setForm({ ...form, contactName: e.target.value })
                }
              />
            </label>
            <label className="auth-label">
              Teléfono
              <input
                value={form.contactPhone}
                onChange={(e) =>
                  setForm({ ...form, contactPhone: e.target.value })
                }
              />
            </label>
          </div>
          <label className="auth-label">
            Descripción
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </label>
          <label className="auth-label auth-label--row">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Activo
          </label>
          {formError ? <p className="auth-error">{formError}</p> : null}
          <div className="inventory-actions">
            <button type="submit" className="auth-button" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
            {editingId ? (
              <button
                type="button"
                className="auth-button auth-button--secondary"
                onClick={resetForm}
              >
                Cancelar edición
              </button>
            ) : null}
          </div>
        </form>
      ) : null}

      {loading ? <p className="auth-muted">Cargando…</p> : null}
      {error ? <p className="auth-error">{error}</p> : null}

      <table className="users-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Contacto</th>
            <th>Estado</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {providers.map((p) => (
            <tr key={p.id}>
              <td>{p.name}</td>
              <td>
                {p.contactName || '—'}
                {p.contactPhone ? ` · ${p.contactPhone}` : ''}
              </td>
              <td>
                <ActiveBadge active={p.active} />
              </td>
              <td>
                {canWrite ? (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => startEdit(p)}
                  >
                    Editar
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!loading && providers.length === 0 ? (
        <p className="auth-muted">No hay proveedores registrados.</p>
      ) : null}
    </section>
  );
}
